import { LuppApi } from '../lupp.service';
import { LUPP_API_BASE_PATH, LUPP_QUERY_KEYS } from '../constants.lupp';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { IncentiveLimits, IncentiveParams } from './dto.incentives';
import { normalizeVehicleTrim } from '../../../../utils/tools';

const percentage = z.number().min(0).max(100);
const incentiveSchema = z.object({
  incentiveIdentifier: z.string(),
  preferredPaymentMethod: z.string(),
  model: z.string(),
  trim: z.string(),
  modelYear: z.number().int().optional(),
  maximumLucidDiscountPercentage: percentage,
  maximumAgentDiscountPercentage: percentage,
  totalPossibleDiscount: percentage,
  isActive: z.boolean(),
  isDeleted: z.boolean(),
  effectiveStartDate: z.iso.datetime(),
  effectiveEndDate: z.iso.datetime(),
});

const normalize = (value: string) =>
  value
    .trim()
    .replace(/^Lucid\s+/i, '')
    .toLowerCase();

/** Fetches and selects the active campaign for the current vehicle and payment method. */
export const fetchIncentiveLimits = async (params: IncentiveParams): Promise<IncentiveLimits> => {
  const requestParams = { ...params, trim: normalizeVehicleTrim(params.trim, params.model) };
  const records = z.array(incentiveSchema).parse(
    await LuppApi.get<unknown>(`${LUPP_API_BASE_PATH}/incentives`, {
      axiosConfig: { params: requestParams },
    })
  );
  const now = Date.now();
  const matches = records.filter(
    (record) =>
      normalize(record.preferredPaymentMethod) === normalize(params.paymentType) &&
      normalize(record.model) === normalize(params.model) &&
      normalize(normalizeVehicleTrim(record.trim, record.model)) ===
        normalize(requestParams.trim) &&
      (record.modelYear === undefined || record.modelYear === params.modelYear) &&
      record.isActive &&
      !record.isDeleted &&
      Date.parse(record.effectiveStartDate) <= now &&
      now <= Date.parse(record.effectiveEndDate)
  );
  const yearMatches = matches.filter((record) => record.modelYear === params.modelYear);
  const applicable = yearMatches.length ? yearMatches : matches;
  if (applicable.length !== 1) {
    throw new Error(
      'Exactly one active incentive campaign is required for this vehicle and payment method.'
    );
  }
  const selected = applicable[0];
  if (
    Math.abs(
      selected.totalPossibleDiscount -
        selected.maximumAgentDiscountPercentage -
        selected.maximumLucidDiscountPercentage
    ) > 1e-8
  ) {
    throw new Error('The incentive campaign contains inconsistent discount limits.');
  }
  return selected;
};

/**
 * Loads limits for the current selection without retaining another selection's data.
 * A matching model year takes precedence over a year-independent campaign.
 * Consumers must block saving while fetching or when the query fails.
 */
export const useGetIncentiveLimits = (params: IncentiveParams) =>
  useQuery({
    queryKey: [LUPP_QUERY_KEYS.INCENTIVES, params],
    queryFn: () => fetchIncentiveLimits(params),
    enabled:
      Boolean(params.paymentType && params.model && params.trim) &&
      Number.isInteger(params.modelYear) &&
      params.modelYear > 0,
    retry: false,
  });

/**
 * Fetches the discount guide using the authenticated sales API.
 * @returns The PDF document for browser preview.
 */
export const fetchDiscountGuidePdf = async (): Promise<Blob> => {
  const response = await LuppApi.instance.get<Blob>(`${LUPP_API_BASE_PATH}/incentives/pdf`, {
    responseType: 'blob',
    headers: { Accept: 'application/pdf' },
  });

  if (!response.data.size) {
    throw new Error('The discount guide PDF is empty.');
  }

  return new Blob([response.data], { type: 'application/pdf' });
};
